import { useMemo } from 'react';

import { useAtomValue } from 'jotai';

import { convertUnit, simpleConvert, Unit } from '@core';
import { useCorridorsV2 } from '@queries';
import { activityUnitAtom, layoutCustomCorridorsAtom, swingMeasuredParametersMapAtom } from '@store';

export const useMergedCorridors = () => {
    const { data: globalCorridors } = useCorridorsV2();
    const activityUnit = useAtomValue(activityUnitAtom);
    const swingMeasuredParametersMap = useAtomValue(swingMeasuredParametersMapAtom);

    const layoutCorridors = useAtomValue(layoutCustomCorridorsAtom);
    const corridorsById = useMemo(() => {
        const allCorridors = Object.fromEntries(
            globalCorridors?.map((corridor) => [
                corridor.swingMeasuredParameters.id,
                {
                    id: corridor.swingMeasuredParameters.id,
                    min: corridor.innerMin,
                    max: corridor.innerMax,
                },
            ]) ?? [],
        );
        return layoutCorridors.reduce((acc, layoutCorridor) => {
            acc[layoutCorridor.swingMeasuredParameterId] = {
                id: layoutCorridor.swingMeasuredParameterId,
                min: layoutCorridor.innerMin,
                max: layoutCorridor.innerMax,
            };
            return acc;
        }, allCorridors);
    }, [globalCorridors, layoutCorridors]);

    return useMemo(() => {
        if (!layoutCorridors) return {};

        return Object.fromEntries(
            Object.entries(corridorsById).map(([id, corridor]) => {
                const unit = swingMeasuredParametersMap[id]?.units?.notation as Unit;
                const max = simpleConvert({
                    unit,
                    value: corridor.max,
                    userPreferredUnit: activityUnit,
                });
                const min = convertUnit({
                    unit,
                    value: corridor.min,
                    userPreferredUnit: activityUnit,
                });
                const converted = {
                    ...corridor,
                    max,
                    min: min.value,
                    displaySymbol: min.symbol,
                    displayUnit: min.unit,
                    unit,
                };
                return [id, converted] as const;
            }),
        );
    }, [activityUnit, corridorsById, layoutCorridors, swingMeasuredParametersMap]);
};
